/**
 * Résumé templates per service. Every service stores its content as JSON:
 *   { [sectionKey]: { field: value } }            for kind 'fields'
 *   { [sectionKey]: [ { field: value }, ... ] }   for kind 'list'
 *   { [sectionKey]: [ 'tag', 'tag' ] }            for kind 'tags'
 * Add a new profession by adding a service row in SQL + an entry here.
 */
export type FieldType = 'text' | 'textarea' | 'month' | 'number' | 'url' | 'email' | 'tel' | 'select' | 'checkbox';

export interface FieldDef {
  key: string; label: string; type?: FieldType; options?: string[]; placeholder?: string; wide?: boolean;
}
export interface SectionDef {
  key: string; title: string; kind: 'fields' | 'list' | 'tags';
  fields?: FieldDef[]; itemLabel?: string; hint?: string; placeholder?: string;
}
export interface ResumeTemplate { code: string; sections: SectionDef[]; }

const PERSONAL: SectionDef = {
  key: 'personal', title: 'Contact Details', kind: 'fields',
  fields: [
    { key: 'fullName', label: 'Full name' },
    { key: 'headline', label: 'Headline / target role', placeholder: 'e.g. Senior Accountant · Registered Nurse · Quarterback' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'phone', label: 'Phone', type: 'tel' },
    { key: 'location', label: 'City, State' },
    { key: 'website', label: 'LinkedIn / website', type: 'url' }
  ]
};

const SUMMARY: SectionDef = {
  key: 'summary', title: 'Professional Summary', kind: 'fields',
  hint: '3–4 lines: who you are, years of experience, strongest skills, what you want next.',
  fields: [{ key: 'text', label: 'Summary', type: 'textarea', wide: true }]
};

const EXPERIENCE: SectionDef = {
  key: 'experience', title: 'Work Experience', kind: 'list', itemLabel: 'Position',
  fields: [
    { key: 'jobTitle', label: 'Job title' },
    { key: 'employer', label: 'Employer' },
    { key: 'location', label: 'Location' },
    { key: 'start', label: 'Start', type: 'month' },
    { key: 'end', label: 'End (blank = Present)', type: 'month' },
    { key: 'description', label: 'Achievements (one per line)', type: 'textarea', wide: true }
  ]
};

const EDUCATION: SectionDef = {
  key: 'education', title: 'Education', kind: 'list', itemLabel: 'School',
  fields: [
    { key: 'degree', label: 'Degree / qualification' },
    { key: 'institution', label: 'School / university' },
    { key: 'location', label: 'Location' },
    { key: 'year', label: 'Year completed' },
    { key: 'details', label: 'Honours, GPA, coursework', type: 'textarea', wide: true }
  ]
};

const SKILLS: SectionDef = { key: 'skills', title: 'Skills', kind: 'tags', placeholder: 'Type a skill and press Enter' };

const CERTS: SectionDef = {
  key: 'certifications', title: 'Certifications & Licences', kind: 'list', itemLabel: 'Certification',
  fields: [{ key: 'name', label: 'Name' }, { key: 'issuer', label: 'Issued by' }, { key: 'year', label: 'Year' }]
};

const AWARDS: SectionDef = {
  key: 'awards', title: 'Awards & Honours', kind: 'list', itemLabel: 'Award',
  fields: [{ key: 'title', label: 'Award' }, { key: 'year', label: 'Year' }, { key: 'description', label: 'Details', type: 'textarea', wide: true }]
};

export const TEMPLATES: { [code: string]: ResumeTemplate } = {
  CIVILIAN: {
    code: 'CIVILIAN',
    sections: [PERSONAL, SUMMARY, EXPERIENCE, EDUCATION, SKILLS, CERTS,
      { key: 'languages', title: 'Languages', kind: 'tags', placeholder: 'e.g. English (fluent)' }]
  },

  STUDENT: {
    code: 'STUDENT',
    sections: [
      PERSONAL,
      { key: 'summary', title: 'Objective', kind: 'fields', hint: 'What you are applying for and why you are a strong fit.',
        fields: [{ key: 'text', label: 'Objective', type: 'textarea', wide: true }] },
      { key: 'education', title: 'Education', kind: 'list', itemLabel: 'School',
        fields: [
          { key: 'institution', label: 'School / college' }, { key: 'degree', label: 'Grade / programme' },
          { key: 'gpa', label: 'GPA' }, { key: 'year', label: 'Graduation year' },
          { key: 'details', label: 'Relevant coursework', type: 'textarea', wide: true }] },
      { key: 'activities', title: 'Activities & Leadership', kind: 'list', itemLabel: 'Activity',
        fields: [{ key: 'name', label: 'Club / team / activity' }, { key: 'role', label: 'Role' }, { key: 'years', label: 'Years' },
                 { key: 'description', label: 'What you did', type: 'textarea', wide: true }] },
      AWARDS,
      { key: 'volunteer', title: 'Volunteer Work', kind: 'list', itemLabel: 'Volunteer role',
        fields: [{ key: 'organization', label: 'Organization' }, { key: 'role', label: 'Role' }, { key: 'hours', label: 'Hours', type: 'number' },
                 { key: 'description', label: 'Details', type: 'textarea', wide: true }] },
      { key: 'experience', title: 'Part-time Jobs & Internships', kind: 'list', itemLabel: 'Job',
        fields: EXPERIENCE.fields },
      SKILLS
    ]
  },

  SPORTS: {
    code: 'SPORTS',
    sections: [
      PERSONAL,
      { key: 'athletic', title: 'Athletic Profile', kind: 'fields',
        fields: [
          { key: 'primarySport', label: 'Primary sport' }, { key: 'position', label: 'Position(s)' },
          { key: 'secondarySport', label: 'Secondary sport' }, { key: 'jersey', label: 'Jersey #' },
          { key: 'height', label: 'Height', placeholder: "6' 1\"" }, { key: 'weight', label: 'Weight', placeholder: '185 lbs' },
          { key: 'dominant', label: 'Dominant hand / foot', type: 'select', options: ['Right', 'Left', 'Both'] },
          { key: 'gradClass', label: 'Graduation class' },
          { key: 'highSchool', label: 'High school' }, { key: 'clubTeam', label: 'Club / travel team' },
          { key: 'headCoach', label: 'Head coach' }, { key: 'ncaaId', label: 'NCAA Eligibility ID' }] },
      { key: 'stats', title: 'Stats & Measurables', kind: 'list', itemLabel: 'Stat',
        fields: [{ key: 'stat', label: 'Stat / test', placeholder: '40-yd dash, PPG, ERA…' }, { key: 'value', label: 'Value' }, { key: 'season', label: 'Season' }] },
      { key: 'videos', title: 'Highlight Videos', kind: 'list', itemLabel: 'Video',
        fields: [{ key: 'title', label: 'Title' }, { key: 'url', label: 'Link (Hudl / YouTube)', type: 'url' }] },
      { key: 'academics', title: 'Academics', kind: 'fields',
        fields: [{ key: 'gpa', label: 'GPA' }, { key: 'testScores', label: 'SAT / ACT' }, { key: 'major', label: 'Intended major' },
                 { key: 'honors', label: 'Academic honours', type: 'textarea', wide: true }] },
      AWARDS,
      { key: 'references', title: 'Coach References', kind: 'list', itemLabel: 'Reference',
        fields: [{ key: 'name', label: 'Name' }, { key: 'team', label: 'Team / school' }, { key: 'phone', label: 'Phone', type: 'tel' }, { key: 'email', label: 'Email', type: 'email' }] }
    ]
  },

  FEDERAL: {
    code: 'FEDERAL',
    sections: [
      PERSONAL,
      { key: 'federal', title: 'Federal Information', kind: 'fields',
        fields: [
          { key: 'citizenship', label: 'Citizenship', type: 'select', options: ['U.S. Citizen', 'U.S. National', 'Other'] },
          { key: 'veteransPreference', label: "Veterans' preference", type: 'select', options: ['None', '5-point', '10-point', 'Sole survivorship'] },
          { key: 'clearance', label: 'Security clearance', placeholder: 'e.g. Secret (active)' },
          { key: 'highestGrade', label: 'Highest grade held', placeholder: 'e.g. GS-0343-12' },
          { key: 'announcement', label: 'Job announcement #' },
          { key: 'availability', label: 'Availability', placeholder: 'Full-time, permanent' }] },
      SUMMARY,
      { key: 'experience', title: 'Work Experience (detailed)', kind: 'list', itemLabel: 'Position',
        hint: 'Federal résumés need hours/week, salary and supervisor for every job.',
        fields: [
          { key: 'jobTitle', label: 'Job title' }, { key: 'employer', label: 'Agency / employer' },
          { key: 'location', label: 'Address' }, { key: 'seriesGrade', label: 'Series / grade' },
          { key: 'start', label: 'Start', type: 'month' }, { key: 'end', label: 'End (blank = Present)', type: 'month' },
          { key: 'hours', label: 'Hours per week', type: 'number' }, { key: 'salary', label: 'Salary' },
          { key: 'supervisor', label: 'Supervisor name & phone' },
          { key: 'mayContact', label: 'May we contact?', type: 'select', options: ['Yes', 'No'] },
          { key: 'description', label: 'Duties & responsibilities', type: 'textarea', wide: true },
          { key: 'accomplishments', label: 'Key accomplishments', type: 'textarea', wide: true }] },
      EDUCATION,
      { key: 'training', title: 'Training', kind: 'list', itemLabel: 'Course',
        fields: [{ key: 'course', label: 'Course' }, { key: 'provider', label: 'Provider' }, { key: 'year', label: 'Year' }] },
      { key: 'ksa', title: 'Knowledge, Skills & Abilities', kind: 'list', itemLabel: 'KSA',
        fields: [{ key: 'title', label: 'KSA' }, { key: 'narrative', label: 'Narrative (Challenge – Context – Action – Result)', type: 'textarea', wide: true }] },
      CERTS, SKILLS
    ]
  },

  TEST_DRIVE: {
    code: 'TEST_DRIVE',
    sections: [PERSONAL, SUMMARY, EXPERIENCE, EDUCATION, SKILLS]
  }
};

export function templateFor(code: string): ResumeTemplate { return TEMPLATES[code] || TEMPLATES.CIVILIAN; }

/** Empty content for a template, with contact details pre-filled from the profile. */
export function blankContent(t: ResumeTemplate, profile: any): any {
  const c: any = {};
  t.sections.forEach(s => { c[s.key] = s.kind === 'fields' ? {} : []; });
  if (profile) {
    c.personal = {
      fullName: [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(' '),
      email: profile.email, phone: profile.cellPhone,
      location: [profile.city, profile.state].filter(Boolean).join(', ')
    };
    if (c.athletic && profile.graduationClass) { c.athletic.gradClass = profile.graduationClass; }
  }
  return c;
}
